import axios, { type AxiosInstance, type AxiosResponse } from 'axios'

import type { IAuthService, IService } from '@/services'
import type { AuthenticationResult } from '@/services'

export abstract class BaseService implements IService {
	protected apiClient: AxiosInstance
	protected authService: IAuthService
	protected scopes: string[]

	constructor(baseURL: string, authService: IAuthService, scopes: string[] = []) {
		this.scopes = scopes
		this.authService = authService
		this.apiClient = axios.create({
			baseURL: baseURL
		})
	}

	protected async addAuthHeader(config: object): Promise<object> {
		return this.authService.getToken(this.scopes).then((res) => {
			return {
				...config,
				headers: {
					Authorization: `Bearer ${res.accessToken}`,
					'Content-Type': 'application/json',
					'Content-Encoding': 'gzip, deflate, br'
				}
			}
		})
	}

	async getToken(): Promise<AuthenticationResult> {
		return this.authService
			.getToken(this.scopes)
			.then((res) => {
				return res
			})
			.catch((err) => {
				console.error(err)
				throw err
			})
	}

	async getData<R>(endpoint: string, params: object = {}, options: object = {}): Promise<R> {
		try {
			const config = { ...options, params: params }
			const reqConfig = await this.addAuthHeader(config)
			const response = await this.apiClient.get<object, AxiosResponse<R>>(endpoint, reqConfig)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	async deleteData<R>(endpoint: string, params: object = {}): Promise<R> {
		try {
			const config = { params: params }
			const reqConfig = await this.addAuthHeader(config)
			const response = await this.apiClient.delete<object, AxiosResponse<R>>(endpoint, reqConfig)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	async postData<T, R = T>(endpoint: string, data: T): Promise<R> {
		try {
			const config = {}
			const reqConfig = await this.addAuthHeader(config)
			const response = await this.apiClient.post<T, AxiosResponse<R>>(endpoint, data, reqConfig)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	async patchData<T, R = T>(endpoint: string, data: T): Promise<R> {
		try {
			const config = {}
			const reqConfig = await this.addAuthHeader(config)
			const response = await this.apiClient.patch<T, AxiosResponse<R>>(endpoint, data, reqConfig)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}

	async putData<T, R = T>(endpoint: string, data: T): Promise<R> {
		try {
			const config = {}
			const reqConfig = await this.addAuthHeader(config)
			const response = await this.apiClient.put<T, AxiosResponse<R>>(endpoint, data, reqConfig)
			return response.data
		} catch (error) {
			console.error(error)
			throw error
		}
	}
}
